import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Header from '../shared/TailwindComponents/Header';
import ListItem from '../shared/TailwindComponents/ListItem';
import { BadgeColor, BadgeItem } from '../shared/TailwindComponents/types/badgeTypes';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import { PiBriefcaseLight, PiTrashLight, PiUserPlusLight, PiShareNetworkLight, PiSpinnerGapLight, PiPlusLight, PiClockLight, PiHourglassLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import { convertLongDateWithTranslation, convertShortDateWithTranslation } from '../shared/DateConverter';
import SkeletonClientItem from '../Clients/SkeletonClientItem';
import Button from '../shared/TailwindComponents/Button';
import { hideModal, showModal } from '../../store/actions/modalActions';
import clsx from 'clsx';
import { getStatusColor, formatDate, formatDateTime } from '../../utils/functions';
import { addMatterCollaborators, deleteMatter, deleteBulkMatters, fetchMatters } from '../../store/actions/Matters/MatterActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import MatterForm from './MatterForm';
import DeleteMatterConfirmation from './DeleteMatterConfirmation';
import { ShareUser } from '../shared/ShareAccessManager';
import { fetchMatterClientRoles, fetchMatterCollaboratorRoles } from '../../store/actions/DropdownActions';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import Flag from '../shared/TailwindComponents/Flag';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import { Tooltip } from '@mui/material';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { FeatureTour, mattersWelcomeTourSteps, mattersOverviewTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

// Country code to country name mapping for flag-icons library
const countryCodeMap: Record<string, string> = {
  'SE': 'SE',
  'US': 'US',
  'UK': 'GB',
  'GB': 'GB',
  'DE': 'DE',
  'FR': 'FR',
  'NO': 'NO',
  'DK': 'DK',
  'FI': 'FI',
};

const statusToColorMap: Record<string, BadgeColor> = {
  Open: 'yellow',
  Active: 'blue',
  Closed: 'gray',
  Discovery: 'blue',
  Negotiation: 'blue',
  Trial: 'blue',
  Intake: 'yellow',
};

const processingStatusToColorMap: Record<string, BadgeColor> = {
  processing: 'blue',
  running: 'blue',
  completed: 'green',
  failed: 'red',
  pending: 'orange',
  queued: 'orange',
};

// Helper to get jurisdiction display
export const getJurisdictionDisplay = (jurisdiction: string | null | undefined) => {
  if (!jurisdiction) return null;
  const code = jurisdiction.toUpperCase();
  const countryCode = countryCodeMap[code] || code;
  return {
    code,
    countryCode,
  };
};

// Portal tooltip component that renders outside table
const TooltipPortal: React.FC<{
  content: string;
  triggerRect: DOMRect;
  isVisible: boolean;
}> = ({ content, triggerRect, isVisible }) => {
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible || !tooltipRef.current) return;

    requestAnimationFrame(() => {
      const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
      const spaceBelow = window.innerHeight - triggerRect.bottom - 10;

      if (spaceBelow < tooltipHeight && triggerRect.top > tooltipHeight + 10) {
        setTooltipPosition('top');
      } else {
        setTooltipPosition('bottom');
      }
    });
  }, [isVisible, triggerRect]);

  const tooltipTop = tooltipPosition === 'bottom'
    ? triggerRect.bottom + 8
    : triggerRect.top - (tooltipRef.current?.offsetHeight || 0) - 8;

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={clsx(
        'fixed pointer-events-none z-50 transition-all duration-200 ease-out',
        isVisible
          ? 'opacity-100 translate-y-0 visible'
          : 'opacity-0 pointer-events-none'
      )}
      style={{
        left: `${Math.max(8, Math.min(triggerRect.left, window.innerWidth - 352))}px`,
        top: `${tooltipTop}px`,
        transform: isVisible
          ? 'translateY(0)'
          : tooltipPosition === 'bottom'
            ? 'translateY(-4px)'
            : 'translateY(4px)',
      }}
    >
      <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs rounded-lg px-4 py-3 max-w-sm shadow-xl border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
        {content}
      </div>
    </div>,
    document.body
  );
};

// Viewport-aware tooltip component for Matter Name
const MatterNameCell: React.FC<{
  matterTitle: string;
  matterType: string;
  matterDescription: string;
  isProcessing: boolean;
  isCompleted: boolean;
  isNew: boolean;
  isFailed: boolean;
  isPending: boolean;
  isQueued: boolean;
  jurisdictionData: any;
  t: any;
  liveStep?: string;
  liveMessage?: string;
  livePercent?: number;
}> = ({
  matterTitle,
  matterType,
  matterDescription,
  isProcessing,
  isCompleted,
  isNew,
  isFailed,
  isPending,
  isQueued,
  jurisdictionData,
  t,
  liveStep,
  liveMessage,
  livePercent,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
      setIsVisible(true);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  return (
    <>
      <div
        className="flex items-center gap-2 relative w-full min-w-0 pr-4"
        style={{ opacity: isFailed ? 0.6 : 1 }}
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {jurisdictionData && isCompleted && (
          <Flag countryCode={jurisdictionData.countryCode} code={jurisdictionData.code} size="sm" />
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          {isProcessing ? (
            // Processing / Running: no title — only show live step info
            <div className="flex items-center gap-2">
              <MiniLoadingToCheckmark isLoading={true} />
              <div className="flex-1 min-w-0">
                {liveStep ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-blue-600 dark:text-blue-400 font-medium truncate">
                        {liveStep.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {liveMessage && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {liveMessage}
                      </div>
                    )}
                    {livePercent != null && livePercent > 0 && (
                      <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-700 transition-all duration-700 ease-in-out rounded-full"
                          style={{ width: `${livePercent}%` }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      {t('Processing matter...')}
                    </div>
                    <div className="h-3 w-2/3 bg-gray-300 dark:bg-gray-600 rounded mt-1 animate-pulseFade"></div>
                  </>
                )}
              </div>
            </div>
          ) : isFailed ? (
            // Failed: show failure indicator, no title
            <div className="flex items-center gap-2">
              <div className="text-sm text-red-500 dark:text-red-400 italic">
                {t('Processing failed')}
              </div>
            </div>
          ) : !isCompleted ? (
            // Pending / Queued / any other non-completed: metadata not ready
            <div className="flex items-center gap-2">
              <PiHourglassLight className="h-4 w-4 text-orange-400 animate-pulse flex-shrink-0" />
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {isPending ? t('Waiting to be queued...') : isQueued ? t('Queued for processing...') : t('Preparing...')}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {t('Details will appear once processing completes')}
                </div>
              </div>
            </div>
          ) : (
            // Completed: show full title + type
            <>
              <div className="text-gray-900 dark:text-gray-100 truncate" title={matterTitle}>
                {matterTitle}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                {matterType || 'No type specified'}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          {isNew && isCompleted && <Badge color="green">{t('New')}</Badge>}
        </div>
      </div>

      {/* Portal-rendered tooltip — only for completed matters */}
      {isCompleted && matterDescription && triggerRect && (
        <TooltipPortal content={matterDescription} triggerRect={triggerRect} isVisible={isVisible} />
      )}
    </>
  );
};

const Matters: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const locationState = location.state as any;

  const allMatters = useSelector((state: RootState) => state.matters.matters || []);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const [isMattersLoading, setIsMattersLoading] = useState(false);
  const [isAddingMatter, setIsAddingMatter] = useState(locationState?.isAddingMatter || false);
  const isAddingMatterRef = useRef(isAddingMatter);
  const historyRef = useRef(history);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const matterListRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const shrinkThreshold = 150;
  const isShrunk = scrollTop >= shrinkThreshold;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedMatters, setSelectedMatters] = useState<string[]>([]);
  const [totalMatters, setTotalMatters] = useState(0);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const collaboratorRoleOptions = useSelector((state: RootState) => state.dropdown.dropdowns.collaboratorRoles || []);

  // ── Feature tour for first-time visitors ──
  const isEmptyState = !isMattersLoading && allMatters.length === 0 && !isAddingMatter && !showTrash;
  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
    startTour: startWelcomeTour,
    hasCompleted: hasCompletedWelcomeTour,
  } = useFeatureTour({
    tourId: 'matters-welcome',
    steps: mattersWelcomeTourSteps,
    readyToShow: isEmptyState,
    autoStartDelay: 800,
  });

  // ── Feature tour for returning visitors with matters ──
  const hasMatters = !isMattersLoading && allMatters.length > 0 && !isAddingMatter && !showTrash;
  const {
    isRunning: isOverviewTourRunning,
    handleJoyrideCallback: handleOverviewTourCallback,
  } = useFeatureTour({
    tourId: 'matters-overview',
    steps: mattersOverviewTourSteps,
    readyToShow: hasMatters,
    autoStartDelay: 800,
  });

  // Audit log side panel state
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditMatter, setAuditMatter] = useState<{ id: string; name: string } | null>(null);

  // Keep refs in sync for use inside stable callbacks
  useEffect(() => { isAddingMatterRef.current = isAddingMatter; }, [isAddingMatter]);
  useEffect(() => { historyRef.current = history; }, [history]);

  // ── PubSub: track active processing matters ──
  // Live processing state per matter UUID (step, message, percent)
  interface LiveProcessingState {
    step?: string;
    message?: string;
    percent?: number;
    status?: string;
  }
  const [processingStates, setProcessingStates] = useState<Record<string, LiveProcessingState>>({});
  const activeGroupsRef = useRef<Set<string>>(new Set());
  const correlationToUuidRef = useRef<Map<string, string>>(new Map());
  const pubsubConnectedRef = useRef(false);
  const fetchRef = useRef<() => void>(() => {});
  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Callback passed to MatterForm — it tells us the PubSub group so the
  // list page can keep listening after MatterForm unmounts.
  const handleMatterGroupJoined = useCallback((groupName: string) => {
    activeGroupsRef.current.add(groupName);
  }, []);

  // ── Handle incoming PubSub messages — route to correct matter's live state ──
  const handleListPubSubMessage = useCallback(
    (raw: any, eventName?: string) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data = { ...event, ...innerData };

      const correlationId = data.correlationId || data.correlation_id || data.CorrelationID || null;
      const matterUuid = data.matter_uuid || data.matterUuid || data.MatterUUID || null;

      const rawStatus = data.status ?? '';
      const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
      const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
      const stepName = data.step_name || data.stepName || data.step || null;
      const message = data.message || '';
      const progressPercent = data.progress_percent;

      // Resolve which matter UUID this event belongs to
      let targetUuid = matterUuid;
      if (!targetUuid && correlationId) {
        targetUuid = correlationToUuidRef.current.get(correlationId);
      }

      const isCompletion = status === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
      const isFailure = status === 'failed' || data.error;

      // Skip technical messages without useful data
      const isTechnicalMessage = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !message;
      if (isTechnicalMessage) return;

      console.log('[Matters PubSub]', { eventName, status, subType, stepName, message, correlationId, targetUuid });

      // Update live processing state for this matter
      if (targetUuid && !isCompletion && !isFailure) {
        setProcessingStates(prev => ({
          ...prev,
          [targetUuid]: {
            step: stepName || prev[targetUuid]?.step,
            message: message || prev[targetUuid]?.message,
            percent: progressPercent ?? prev[targetUuid]?.percent,
            status: status || prev[targetUuid]?.status,
          },
        }));
      }

      if (isCompletion || isFailure) {
        console.log('[Matters PubSub] Detected completion/failure — scheduling refetch');

        // Clear live state for this matter
        if (targetUuid) {
          setProcessingStates(prev => {
            const next = { ...prev };
            delete next[targetUuid];
            return next;
          });
        }

        // Leave the group since processing is done
        if (correlationId) {
          const groupName = `matter_${correlationId}`;
          const multiHubService = getMultiHubSocketService();
          multiHubService.emit('ai_processing', 'leave', { group: groupName });
          activeGroupsRef.current.delete(groupName);
        }

        // Navigate to the completed matter if the user was creating one
        // (MatterForm's handler won't fire if it unmounted before completion)
        const completedMatterUuid = targetUuid || matterUuid;
        if (isCompletion && completedMatterUuid && isAddingMatterRef.current) {
          console.log('[Matters PubSub] Navigating to completed matter:', completedMatterUuid);
          // Small delay to let the refetch start first
          setTimeout(() => {
            historyRef.current.push(`/matters/${completedMatterUuid}`);
          }, 500);
          return;
        }

        // Debounced refetch — if multiple matters complete in quick succession,
        // only refetch once after a short delay
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          fetchRef.current();
        }, 1500);
      }
    },
    []
  );

  // ── Connect to PubSub hub once on mount, clean up on unmount ──
  useEffect(() => {
    let mounted = true;

    const connectPubSub = async () => {
      const multiHubService = getMultiHubSocketService();
      const connected = await multiHubService.connectToHub('ai_processing');
      if (connected && mounted) {
        pubsubConnectedRef.current = true;
        multiHubService.on('ai_processing', '*', handleListPubSubMessage);
      }
    };

    connectPubSub();

    return () => {
      mounted = false;
      const multiHubService = getMultiHubSocketService();

      // Leave all tracked groups
      for (const group of activeGroupsRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group });
      }
      activeGroupsRef.current.clear();
      correlationToUuidRef.current.clear();

      multiHubService.off('ai_processing', '*', handleListPubSubMessage);
      multiHubService.disconnectHub('ai_processing');
      pubsubConnectedRef.current = false;

      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [handleListPubSubMessage]);

  // ── Join PubSub groups for all active processing matters ──
  // This runs after each fetch to pick up new processing matters
  const joinActiveProcessingGroups = useCallback((matters: any[]) => {
    if (!pubsubConnectedRef.current) return;
    const multiHubService = getMultiHubSocketService();

    const activeStatuses = new Set(['processing', 'running', 'queued', 'pending']);

    matters.forEach((matter: any) => {
      const status = matter.processing_status || 'pending';
      const correlationId = matter.processing_correlation_id;
      const matterUuid = matter.uuid || matter.MatterID;

      if (activeStatuses.has(status) && correlationId) {
        const groupName = `matter_${correlationId}`;

        // Map correlation ID → matter UUID for routing events
        correlationToUuidRef.current.set(correlationId, matterUuid);

        if (!activeGroupsRef.current.has(groupName)) {
          console.log(`[Matters PubSub] Joining group ${groupName} for matter ${matterUuid} (status: ${status})`);
          multiHubService.emit('ai_processing', 'join', { group: groupName });
          activeGroupsRef.current.add(groupName);
        }
      }
    });
  }, []);

  const { checkSectionGate } = useLicenseGate();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      if (checkSectionGate('matters')) {
        setIsAddingMatter(true);
      } else {
        // Remove query param so user isn't stuck
        history.replace('/matters');
      }
    } else {
      setIsAddingMatter(false);
    }
  }, [location]);

  const fetchInitialMatters = useCallback(async () => {
    setIsMattersLoading(true);
    setPage(1);
    const resp = await dispatch(fetchMatters({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: 1,
      page_size: 50,
    }));

    const { matters, currentPage, total_matters } = resp;
    setPage(currentPage);
    const nextPageSize = matters.length;
    const totalPages = Math.ceil(total_matters / 50);
    const isLastPage = currentPage >= totalPages || nextPageSize < 50;
    setHasMore(!isLastPage);
    setTotalMatters(total_matters);
    setSelectedMatters([]);
    setIsMattersLoading(false);

    // Join PubSub groups for any actively processing matters
    joinActiveProcessingGroups(matters);
  }, [dispatch, searchQuery, selectedFilters, sortBy, sortOrder, joinActiveProcessingGroups]);

  // Keep fetchRef in sync so PubSub callback always calls latest version
  useEffect(() => {
    fetchRef.current = fetchInitialMatters;
  }, [fetchInitialMatters]);

  const loadMoreMatters = useCallback(async () => {
    setIsMattersLoading(true);
    const next = page + 1;

    const resp = await dispatch(fetchMatters({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: next,
      page_size: 50,
    }));

    const { matters, currentPage, total_matters } = resp;
    setPage(currentPage);
    const nextPageSize = matters.length;
    const totalPages = Math.ceil(total_matters / 50);
    const isLastPage = currentPage >= totalPages || nextPageSize < 50;
    setHasMore(!isLastPage);
    setIsMattersLoading(false);
  }, [dispatch, page, searchQuery, selectedFilters, sortBy, sortOrder]);

  useEffect(() => {
    setPage(1);
    // dispatch(fetchMatterClientRoles())
    // dispatch(fetchMatterCollaboratorRoles())
    setSelectedMatters([]);
    fetchInitialMatters();
  }, [searchQuery, selectedFilters, sortBy, sortOrder, fetchInitialMatters]);

  // ── Refetch whenever the user navigates back to the list view ──
  const prevIsAddingRef = useRef(isAddingMatter);
  useEffect(() => {
    // Detect transition from adding → list (form closed / navigated back)
    if (prevIsAddingRef.current && !isAddingMatter) {
      // Safety: re-register the list PubSub handler in case MatterForm's unmount
      // somehow removed it (the targeted off() should prevent this, but belt-and-suspenders).
      if (pubsubConnectedRef.current) {
        const multiHubService = getMultiHubSocketService();
        // Remove first to avoid duplicate registrations, then re-add
        multiHubService.off('ai_processing', '*', handleListPubSubMessage);
        multiHubService.on('ai_processing', '*', handleListPubSubMessage);
      }
      fetchInitialMatters();
    }
    prevIsAddingRef.current = isAddingMatter;
  }, [isAddingMatter, fetchInitialMatters, handleListPubSubMessage]);

  // Refetch when navigating back to /matters from a sub-page (location changes)
  const prevLocationRef = useRef({ key: location.key, pathname: location.pathname });
  useEffect(() => {
    const prev = prevLocationRef.current;
    const isMattersListPage = location.pathname === '/matters';
    const locationChanged = prev.key !== location.key || prev.pathname !== location.pathname;
    const wasOnSubPage = prev.pathname !== '/matters' && prev.pathname.startsWith('/matters/');

    // Refetch if: navigating back to /matters list from a different page/key
    if (isMattersListPage && locationChanged && !isAddingMatter) {
      fetchInitialMatters();
    }

    prevLocationRef.current = { key: location.key, pathname: location.pathname };
  }, [location.key, location.pathname, isAddingMatter, fetchInitialMatters]);

  const handleScroll = () => {
    const scrollPosition = matterListRef.current?.scrollTop || 0;
    setScrollTop(scrollPosition);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterName]: selectedValues,
    }));
  };

  const handleSortChange = (newSortBy: string, newSortOrder?: 'asc' | 'desc') => {
    // Handle both direct calls from table headers (with two params)
    // and calls from FilterBarWithTabs (with encoded value like "field_order")
    if (newSortOrder) {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
    } else {
      // Decode the sort value from FilterBarWithTabs (format: "field_order")
      const parts = newSortBy.split('_');
      const order = parts.pop() as 'asc' | 'desc';
      const field = parts.join('_');
      setSortBy(field);
      setSortOrder(order);
    }
  };

  const handleAddMatterClick = () => {
    setIsAddingMatter(true);
    history.push('/matters?add=true');
  };

  const handleAddMatterClose = () => {
    setIsAddingMatter(false);
    history.push('/matters');
  };

  const handleMatterClick = (matterItem: any) => {
    // Only allow clicking into matters that have completed processing
    const processingStatus = matterItem.processing_status;
    if (processingStatus && processingStatus !== 'completed') {
      // Show a subtle feedback message depending on state
      if (processingStatus === 'processing' || processingStatus === 'running') {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('This matter is still being processed. Please wait until processing completes.'),
          modalType: 'info',
          duration: 3000,
        }));
      } else if (processingStatus === 'failed') {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('This matter failed to process. Try restarting the process.'),
          modalType: 'warning',
          duration: 3000,
        }));
      }
      return;
    }
    // Use UUID from matter data for navigation (matterId is the numeric ID, uuid is the UUID)
    const matterUuid = matterItem.uuid || matterItem.MatterID;
    history.push(`/matters/${matterUuid}`);
  };

  const handleRestartMatter = (matterItem: any, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    dispatch(showModal({
      type: 'confirmation',
      message: t('Restart Matter Processing'),
      subMsg: t('This will restart the AI processing pipeline for this matter. Any in-progress analysis will be reset.'),
      onConfirm: async () => {
        try {
          // TODO: Call the restart API endpoint here
          // await dispatch(restartMatterProcessing(matterItem.id));
          dispatch(showFeedbackModal({
            modalType: 'success',
            message: t('Matter processing restarted successfully.'),
            showModal: true,
            duration: 3000,
          }));
          fetchInitialMatters();
        } catch (error) {
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to restart matter processing.'),
            showModal: true,
            duration: 3000,
          }));
        }
        dispatch(hideModal());
      },
      onCancel: () => { },
      showModal: true,
    }));
  };

  const filters = [
    {
      id: 'MatterStage',
      name: t('Matter Stage'),
      options: [
        { value: 'Intake/Pre-Dispute', label: t('Intake') },
        { value: 'Discovery', label: t('Discovery') },
        { value: 'Negotiation', label: t('Negotiation') },
        { value: 'Trial', label: t('Trial') },
        { value: 'Closed', label: t('Closed') },
      ],
    },
    {
      id: 'status',
      name: t('Status'),
      options: [
        { value: 'open', label: t('Open') },
        { value: 'active', label: t('Active') },
        { value: 'closed', label: t('Closed') },
      ],
    },
  ];

  const sortOptions = [
    { value: 'created_at_asc', label: t('Created At (Oldest First)'), current: sortBy === 'created_at' && sortOrder === 'asc' },
    { value: 'created_at_desc', label: t('Created At (Newest First)'), current: sortBy === 'created_at' && sortOrder === 'desc' },
    { value: 'matter_name_asc', label: t('Matter Name (A-Z)'), current: sortBy === 'matter_name' && sortOrder === 'asc' },
    { value: 'matter_name_desc', label: t('Matter Name (Z-A)'), current: sortBy === 'matter_name' && sortOrder === 'desc' },
  ];

  const handleClearAllFilters = () => {
    setSelectedFilters({});
    setSearchQuery('');
    setSortBy('');
    setSortOrder('asc');
  };

  const handleDeleteMatters = (matterIds: string[]) => {
    if (matterIds.length === 0) {
      dispatch(showFeedbackModal({
        modalType: 'warning',
        message: t('Please select at least one matter.'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    dispatch(showModal({
      type: 'custom',
      message: t(`Are you sure you want to delete ${matterIds.length > 1 ? 'these matters' : 'this matter'}?`),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      content: (
        <DeleteMatterConfirmation
          matterCount={matterIds.length}
          onConfirm={async ({ deleteTasks, deleteFilesFolders }) => {
            let result;
            if (matterIds.length > 1) {
              result = await dispatch(deleteBulkMatters(matterIds, { deleteTasks, deleteFilesFolders }) as any);
            } else {
              result = await dispatch(deleteMatter(matterIds[0], { deleteTasks, deleteFilesFolders }) as any);
            }
            if (result) {
              fetchInitialMatters();
              setSelectedMatters([]);
            }
            dispatch(hideModal());
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => { },
      showModal: true,
    }));
  };

  const handleAddCollaborator = (matterId: string, collaborators: ShareUser[]) => {
    dispatch(showModal({
      showModal: true,
      type: 'giveAccess',
      onSave: async (nextShared: ShareUser[]) => {
        await dispatch(addMatterCollaborators({
          MatterID: matterId,
          Collaborators: nextShared,
          Permission: 'write',
          Roles: ['Attorney'],
        }));
        fetchInitialMatters(); 
        dispatch(hideModal());
      },
      message: t('Add Collaborator'),
      subMsg: t('Select users to add as collaborators to this matter.'),
      employees: [],
      initialShared: collaborators,
      roles: collaboratorRoleOptions,
      icon: <PiShareNetworkLight className="h-5 w-5 text-primary-600" />,
    }));
  };

  const matterActions = [
    {
      label: 'Delete Matters',
      onClick: (matterIds: string[]) => handleDeleteMatters(matterIds),
      icon: <PiTrashLight className="h-4 w-4" />,
    },
  ];

  const tableActions = [
    // {
    //   label: t('Add Collaborator'),
    //   onClick: (row: any) => handleAddCollaborator(row.id,row.matterCollaborators),
    //   icon: <PiUserPlusLight className="h-4 w-4" />,
    // },
    {
      label: t('Activity Log'),
      onClick: (row: any) => {
        const matter = allMatters.find((m: any) => (m.uuid || m.MatterID) === row.id);
        const name = matter?.title || matter?.MatterName || '';
        setAuditMatter({ id: row.id, name });
        setShowAuditLog(true);
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete Matter'),
      onClick: (row: any) => handleDeleteMatters([row.id]),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const columns = useMemo(
    () => [
      'Select',
      'Name',
      'Stage',
      'Status',
      // 'Creation Status',
      'Created At',
      'Clients',
      'Access',
      'Actions',
    ],
    []
  );

  const sortableColumns = ['Name', 'Created At'];

  const tableData = useMemo(() => {
    const now = new Date();
    return allMatters.map((matterItem: any) => {
      const createdDate = new Date(matterItem.created_at);
      const isNew = (now.getTime() - createdDate.getTime()) / 3600000 < 24;
      const processingStatus = matterItem.processing_status || 'pending';
      const isFailed = processingStatus === 'failed';
      const isProcessing = processingStatus === 'processing' || processingStatus === 'running';
      const isCompleted = processingStatus === 'completed' || processingStatus === 'open';
      const isPending = processingStatus === 'pending';
      const isQueued = processingStatus === 'queued';
      const isClickable = isCompleted;

      const documentCount = matterItem.DocumentCount || 0;
      const documentText = documentCount > 0 ? `${documentCount} document${documentCount > 1 ? 's' : ''}` : 'No documents';

      // Use UUID for ID if available, otherwise fall back to numeric ID
      const matterUuid = matterItem.uuid || matterItem.MatterID;

      // Get title from AI extraction or fallback to MatterName
      const matterTitle = matterItem.title || matterItem.MatterName || `Matter ${matterItem.MatterID}`;
      const matterType = matterItem.type || '';
      const matterDescription = matterItem.description || '';
      const jurisdictionData = getJurisdictionDisplay(matterItem.jurisdiction);

      // Get live processing state from PubSub
      const liveState = processingStates[matterUuid];

      return {
        id: matterUuid,
        matterTitle: matterTitle,
        processing_status: processingStatus,
        _isClickable: isClickable,
        'Name': (
          <MatterNameCell
            matterTitle={matterTitle}
            matterType={matterType}
            matterDescription={matterDescription}
            isProcessing={isProcessing}
            isCompleted={isCompleted}
            isNew={isNew}
            isFailed={isFailed}
            isPending={isPending}
            isQueued={isQueued}
            jurisdictionData={jurisdictionData}
            t={t}
            liveStep={liveState?.step}
            liveMessage={liveState?.message}
            livePercent={liveState?.percent}
          />
        ),
        Stage: !isCompleted ? (
          <span className="text-xs text-gray-400 italic">—</span>
        ) : (
          <Badge className='capitalize' color={getStatusColor(matterItem.stage_name || matterItem.MatterStage) as BadgeColor}>
            {matterItem.stage_name || matterItem.MatterStage || t('No stage')}
          </Badge>
        ),
        'Status': !isCompleted ? (
          <span className="text-xs text-gray-400 italic">—</span>
        ) : (
          <Badge className='capitalize' color={getStatusColor(matterItem.status_name) as BadgeColor}>
            {matterItem.status_name || t('No status')}
          </Badge>
        ),
        // 'Creation Status': (
        //   <div className="flex items-center gap-2">
        //     <Badge
        //       className='capitalize'
        //       color={processingStatusToColorMap[processingStatus]}
        //       tooltip={isCompleted && matterItem.processing_completed_at ? `${t('Completed')}: ${formatDateTime(matterItem.processing_completed_at)}` : ''}
        //       icon={
        //         isProcessing ? <PiSpinnerGapLight className="animate-spin" /> :
        //         (isPending || isQueued) ? <PiClockLight className="text-orange-400" /> :
        //         undefined
        //       }
        //     >
        //       {isProcessing ? t('Processing') :
        //        isQueued ? t('Queued') :
        //        isPending ? t('Pending') :
        //        processingStatus}
        //     </Badge>
        //     {isFailed && (
        //       <Button
        //         variant="tertiary"
        //         size="small"
        //         disabled
        //         onClick={(e) => handleRestartMatter(matterItem, e)}
        //         tooltip={t('Retry processing this matter')}
        //         className="opacity-70 hover:opacity-100 transition-opacity"
        //       >
        //         {t('Retry')}
        //       </Button>
        //     )}
        //   </div>
        // ),
        'Created At': formatDateTime(matterItem.created_at),
        'Clients': matterItem.matter_clients?.clients?.length ? (
          <div className="flex items-center">
            {matterItem.matter_clients.clients.slice(0, 4).map((client: any, idx: number) => (
              <div
                key={client.client_uuid}
                className={clsx(
                  'relative rounded-full',
                  idx > 0 && 'ml-[-0.5rem]'
                )}
              >
                <Tooltip title={client.name || 'Unknown Client'}>
                  <div
                    className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors"
                    onClick={() => history.push(`/contacts/${client.client_uuid}`)}
                  >
                    {(client.name || 'U')[0].toUpperCase()}
                  </div>
                </Tooltip>
              </div>
            ))}
            {matterItem.matter_clients.clients.length > 4 && (
              <span className="text-sm text-gray-500 ml-1">+{matterItem.matter_clients.clients.length - 4}</span>
            )}
          </div>
        ) : '-',
        'matterCollaborators': matterItem.Collaborators || [],
        'Access': (
          <div className="flex gap-3 items-center">
            {(matterItem.Collaborators && matterItem.Collaborators.length > 0) && (
              <div className="flex items-center">
                {(matterItem.Collaborators || []).slice(0, 3).map((collab: ShareUser, idx: number) => (
                  <div
                    key={idx}
                    className={clsx(
                      'relative rounded-full',
                      idx > 0 && 'ml-[-0.5rem]'
                    )}
                  >
                    <CollaboratorBadge collaborator={collab} />
                  </div>
                ))}
                {(matterItem.Collaborators && matterItem.Collaborators.length > 3) && (
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <div className="text-xs leading-5">
                        {(matterItem.Collaborators || [])
                          .slice(3)
                          .map((c: any, i: number) => (
                            <div key={i}>
                              {c.FullName || c.full_name || c.name || c.email || 'Unknown user'}
                            </div>
                          ))}
                      </div>
                    }
                  >
                    <div className="relative ml-[-0.5rem] cursor-default">
                      <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm text-gray-700 dark:text-white font-medium">
                        +{(matterItem.Collaborators || []).length - 3}
                      </div>
                    </div>
                  </Tooltip>
                )}
              </div>
            )}

          </div>
        ),

      };
    });
  }, [allMatters, t, handleRestartMatter, processingStates]);

  const gridData = useMemo(() => {
    return allMatters.map((matterItem: any) => {
      const createdDate = new Date(matterItem.created_at);
      const isNew = (new Date().getTime() - createdDate.getTime()) / 3600000 < 24;
      const processingStatus = matterItem.processing_status || 'pending';
      const isProcessing = processingStatus === 'running' || processingStatus === 'processing';
      const isCompleted = processingStatus === 'completed';
      const isPending = processingStatus === 'pending';
      const isQueued = processingStatus === 'queued';
      const isFailed = processingStatus === 'failed';
      const isClickable = isCompleted;

      // Get live state
      const matterUuid = matterItem.uuid || matterItem.MatterID;
      const liveState = processingStates[matterUuid];

      const badges: BadgeItem[] = [];
      if (isCompleted) {
        badges.push({
          label: matterItem.stage_name,
          color: statusToColorMap[matterItem.stage_name] || 'gray',
        });
      }
      badges.push({
        label: formatDate(matterItem.created_at),
        color: 'gray',
      });
      badges.push({
        label: isProcessing ? (liveState?.step ? liveState.step.replace(/_/g, ' ') : 'Processing...') :
               isQueued ? t('Queued') :
               isPending ? t('Pending') :
               processingStatus,
        color: processingStatusToColorMap[processingStatus],
        tooltip: isCompleted && matterItem.processing_completed_at ? `${t('Completed')}: ${formatDateTime(matterItem.processing_completed_at)}` : undefined,
        icon: isProcessing ? <PiSpinnerGapLight className="animate-spin" /> :
              (isPending || isQueued) ? <PiClockLight /> : undefined,
      });
      if (isNew && isClickable) badges.push({ label: 'New', color: 'green' });

      const documentCount = matterItem.DocumentCount || 0;
      const documentText = documentCount > 0 ? `${documentCount} document${documentCount > 1 ? 's' : ''}` : t('No documents');

      // Get title from AI extraction or fallback to MatterName
      const matterTitle = !isCompleted
        ? t('Matter being prepared...')
        : (matterItem.title || matterItem.MatterName || `Matter ${matterItem.MatterID}`);
      const matterType = matterItem.type || '';
      const matterDescription = matterItem.description || '';
      const jurisdictionData = getJurisdictionDisplay(matterItem.jurisdiction);
      const matterLanguage = matterItem.language || '';

      const details = !isCompleted ? [] : [
        ...(jurisdictionData ? [{ label: 'Jurisdiction', value: jurisdictionData.code }] : []),
        ...(matterLanguage ? [{ label: 'Language', value: matterLanguage }] : []),
        ...(matterType ? [{ label: 'Matter Type', value: matterType }] : []),
        ...(matterItem.client?.name ? [{ label: 'Client', value: `${matterItem.client.name}${matterItem.client.email ? ` (${matterItem.client.email})` : ''}` }] : []),
        ...(matterItem.FinancialData?.DueDate
          ? [
            {
              label: 'Due Date',
              value: formatDate(matterItem.FinancialData.DueDate),
            },
          ]
          : []),
          {label: "Access", value: matterItem.Collaborators}
      ];

      // Subtitle with type and description preview
      const subtitleParts = [];
      if (isProcessing && liveState?.message) {
        subtitleParts.push(liveState.message);
      } else if (isCompleted) {
        if (matterType) {
          subtitleParts.push(matterType);
        }
        if (matterDescription) {
          subtitleParts.push(matterDescription.length > 80 ? `${matterDescription.substring(0, 80)}...` : matterDescription);
        }
      }
      const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' • ') : '';

      return (
        <ListItem
          key={matterUuid}
          title={matterTitle}
          subtitle={subtitle}
          details={details}
          badges={badges}
          onClick={() => handleMatterClick(matterItem)}
          link={isClickable ? `/matters/${matterUuid}` : undefined}
          collaborators={matterItem.Collaborators || []}
          currentUserId={matterItem.UserID}
          icon={<PiBriefcaseLight className="text-gray-500 dark:text-gray-400" size={18} />}
          style={{
            opacity: !isCompleted ? 0.7 : 1,
            cursor: isClickable ? 'pointer' : 'not-allowed',
          }}
          processingStatus={processingStatus}
          processingCompletedAt={matterItem.processing_completed_at}
        />
      );
    });
  }, [allMatters, t, processingStates]);

  interface GridProps {
    gridData: React.ReactNode[];
    isMattersLoading: boolean;
    hasMore: boolean;
    loadMoreMatters: () => void;
  }

  const GridView: React.FC<GridProps> = ({ gridData, isMattersLoading, hasMore, loadMoreMatters }) => {
    const gridContainerRef = useRef<HTMLDivElement | null>(null);

    return (
      <div ref={gridContainerRef} className="px-4 overflow-y-auto h-full max-h-[calc(100vh-120px)] custom-scrollbar-main flex flex-col">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2 pb-2 flex-grow">
          {gridData}
          {isMattersLoading && Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm animate-pulseFade"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulseFade" />
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3 animate-pulseFade" />
                </div>
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-16 animate-pulseFade" />
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-full animate-pulseFade" />
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-5/6 animate-pulseFade" />
              </div>
              <div className="flex gap-2 mt-3">
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded-full w-12 animate-pulseFade" />
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded-full w-12 animate-pulseFade" />
              </div>
            </div>
          ))}
          {gridData.length === 0 && !isMattersLoading && (
            <div className="col-span-full flex flex-col justify-center items-center h-full gap-4 max-w-md mx-auto text-center py-20">
              <div className="text-gray-400 dark:text-gray-500">
                <PiBriefcaseLight size={48} />
              </div>
              <h2 className="text-lg text-gray-900 dark:text-gray-100">
                {t('You have no matters listed.')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('Initiate by creating a new matter to organize details, track progress, and collaborate with your team.')}
              </p>
              <Button
                variant="primary"
                size="small"
                onClick={handleAddMatterClick}
              >
                {t('Create New Matter')}
              </Button>
            </div>
          )}
        </div>
        {hasMore && gridData.length > 0 && (
          <div className="flex justify-center py-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              size="small"
              onClick={loadMoreMatters}
              disabled={isMattersLoading}
              isLoading={isMattersLoading}
            >
              {isMattersLoading ? t('Loading...') : t('Load More')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <div className="flex flex-col overflow-hidden relative">
      <div className={`${isShrunk && !isAddingMatter ? 'sticky top-[80px] z-20' : ''}`}>
        {!isAddingMatter && (
          <div data-tour-id="matters-add-button">
            <Header
              actions={[
                {
                  label: 'Add Matter',
                  icon: <PiPlusLight className='w-4 h-4' />,
                  onClick: handleAddMatterClick,
                },
              ]}
              subscriptionActive={subscriptionActive}
            />
          </div>
        )}
        {!isAddingMatter && (
          <div data-tour-id="matters-search-bar">
          <FilterBarWithTabs
            filters={filters}
            sortOptions={viewMode === 'grid' ? sortOptions : []}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onSortChange={viewMode === 'grid' ? handleSortChange : undefined}
            viewMode={viewMode}
            setViewMode={setViewMode}
            onSearchChange={handleSearchChange}
            sortValue={`${sortBy}_${sortOrder}`}
            onClearAllFilters={handleClearAllFilters}
            actions={[
              ...matterActions.map(action => ({
                label: action.label,
                onClick: () => action.onClick(selectedMatters),
                icon: action.icon,
              })),
              ...(showTrash ? trashBulkActions : []),
            ]}
            selectionCount={showTrash ? trashSelectedCount : (selectedMatters.length > 0 ? selectedMatters.length : undefined)}
            recycleBinEnabled
            recycleBinActive={showTrash}
            recycleBinLabel={t('Matters')}
            onRecycleBinToggle={(active) => { if (!active) fetchInitialMatters(); setShowTrash(active); }}
          />
          </div>
        )}
      </div>
      {showTrash ? (
        <ResourceTrash
          entityType="matter"
          entityLabel="matters"
          emptyIcon={<PiBriefcaseLight />}
          onItemRestored={() => { setPage(1); setHasMore(true); }}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      <div className="flex-grow flex flex-col" ref={matterListRef} onScroll={handleScroll}>

        <div className="flex-grow overflow-y-auto">
          {isAddingMatter ? (
            <div className="h-screen">
              <MatterForm
                onClose={handleAddMatterClose}
                clientId={locationState?.clientId}
                onGroupJoined={handleMatterGroupJoined}
              />
            </div>
          ) : (
            <div className="animate-fade-in">
              {viewMode === 'grid' ? (
                <GridView
                  gridData={gridData}
                  isMattersLoading={isMattersLoading}
                  hasMore={hasMore}
                  loadMoreMatters={loadMoreMatters}
                />
              ) : (
                <div data-tour-id="matters-table">
                  {isMattersLoading && allMatters.length === 0 ? (
                    <SkeletonClientItem viewMode="table" count={3} />
                  ) : (
                    <Table
                      columns={columns}
                      data={tableData}
                      enableSelection
                      selectedIds={selectedMatters}
                      onSelect={(id) => setSelectedMatters(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                      onSelectAll={() => setSelectedMatters(selectedMatters.length === allMatters.length ? [] : allMatters.map(m => m.uuid || m.MatterID))}
                      onRowClick={(row) => {
                        // Only allow navigation for completed matters
                        const status = row.processing_status;
                        if (status && status !== 'completed') {
                          // Find the matter item to show proper feedback
                          const matterItem = allMatters.find((m: any) => (m.uuid || m.MatterID) === row.id);
                          if (matterItem) handleMatterClick(matterItem);
                          return;
                        }
                        history.push(`/matters/${row.uuid || row.id}`);
                      }}
                      actions={tableActions}
                      hasMore={hasMore}
                      isLoadingMore={isMattersLoading}
                      onLoadMore={loadMoreMatters}
                      sortableColumns={sortableColumns}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSortChange}
                      total_items={totalMatters}
                      total_showing_items={allMatters.length}
                      setIsShowing={true}
                      showingLabel={t('matters')}
                      selectedCount={selectedMatters.length}
                      resizableColumns={true}
                      defaultColumnWidths={{
                        'Name': 250,
                        'Stage': 150,
                        'Status': 150,
                        // 'Creation Status': 150,
                        'Created At': 180,
                        'Client': 200,
                        'Access': 180,
                      }}
                      emptyState={{
                        icon: <PiBriefcaseLight />,
                        headline: t('You have no matters listed.'),
                        subtext: t('Initiate by creating a new matter to organize details, track progress, and collaborate with your team.'),
                        button: {
                          label: t('Create New Matter'),
                          onClick: handleAddMatterClick,
                        }
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )}
    </div>

    {/* Audit Log Side Panel with Backdrop */}
    {showAuditLog && auditMatter && (
      <>
        <div
          className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
          onClick={() => setShowAuditLog(false)}
        />
        <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
          <AuditLogPanel
            entityType="matter"
            entityId={auditMatter.id}
            entityName={auditMatter.name}
            onClose={() => {
              setShowAuditLog(false);
              setAuditMatter(null);
            }}
          />
        </div>
      </>
    )}

    {/* Onboarding feature tour */}
    <FeatureTour
      steps={mattersWelcomeTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    <FeatureTour
      steps={mattersOverviewTourSteps}
      run={isOverviewTourRunning}
      onCallback={handleOverviewTourCallback}
      disableOverlay
    />
    </>
  );
};

export default Matters;
